import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  SimpleChanges,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import type { HttpEvent } from '@angular/common/http';
import { KostDataImportProcessData } from '../import-process';
import {
  BdUiInlineNotificationComponent,
  BdUiStepProgressComponent,
} from '@bindoc-projects/ui-lib/feedback/ui';
import { BdWindowRefService } from '@bindoc-projects/platform-lib/data';
import { Observable } from 'rxjs';
import { KostDataImportNewUploadArtifactsComponent } from './artifacts/artifacts.component';
import { Button } from 'primeng/button';
import { Message } from 'primeng/message';
import {
  chainHeading,
  type BdJobChainResponse,
  type BdJobChainSubject,
} from '@bindoc-projects/job-lib/logic';
import {
  BdJobChainHandle,
  BdJobChainTracker,
} from '@bindoc-projects/job-lib/data';
import { BdUiChainStatusComponent } from '@bindoc-projects/job-lib/ui';
import {
  KostDataImportUploadState,
  uploadEvent,
  uploadSteps,
} from './upload-progress';

export interface KostDataImportUploadConfig {
  /** The upload with `reportProgress: true, observe: 'events'`. */
  uploader: () => Observable<HttpEvent<unknown>>;
  /**
   * The job chain the upload starts, from the upload response. With it the
   * step follows the processing instead of only confirming the transfer.
   */
  chainSubject?: (uploaded: unknown) => BdJobChainSubject | null;
}

/**
 * Last wizard step: the transfer starts here and cannot be undone, so the
 * step has no way back. The transfer and the processing after it show as
 * `bd-ui-step-progress`, like the chain status that takes over.
 */
@Component({
  selector: 'kost-data-import-new-upload',
  template: `
    <div class="bd-container">
      @switch (state()) {
        @case ('init') {
          <div class="flex flex-col gap-6">
            <span i18n
              >Der anonymisierte Datensatz kann jetzt an die BinDoc GmbH
              übertragen werden</span
            >

            @if (artifacts.length > 0) {
              <div>
                <kost-data-import-new-upload-artifacts [artifacts]="artifacts">
                </kost-data-import-new-upload-artifacts>
              </div>
            }
            <div class="bd-container-inner flex justify-center">
              <p-button
                label="Anonymisierten Datensatz an BinDoc übertragen"
                i18n-label
                severity="primary"
                [pt]="{ root: { 'data-test': 'kost-upload-submit' } }"
                (onClick)="onSubmit()"
              />
            </div>
          </div>
        }
        @case ('uploading') {
          <bd-ui-step-progress
            data-test="kost-upload-progress"
            heading="Datensatz wird übertragen"
            [steps]="steps()"
          />
        }
        @case ('failed') {
          <bd-ui-step-progress
            data-test="kost-upload-progress"
            heading="Übertragung abgebrochen"
            [steps]="steps()"
          />
          <bd-ui-inline-notification
            class="mt-4 block"
            data-test="kost-upload-error"
            severity="error"
            variant="compact"
            title="Datensatz übertragen fehlgeschlagen"
            message="Der Datensatz konnte nicht an BinDoc übertragen werden. Ihre Auswahl bleibt erhalten."
            actionLabel="Erneut übertragen"
            actionIcon="pi pi-refresh"
            (action)="onSubmit()"
          />
        }
        @case ('done') {
          @if (chain; as chain) {
            <bd-ui-chain-status
              class="mb-4 block"
              [chain]="chain.chain()"
              [unavailable]="chain.state() === 'unavailable'"
              [title]="heading(chain.chain())"
              unavailableMessage="Der Datensatz wurde übertragen und wird im Hintergrund verarbeitet. Die Daten sind in Kürze in Ihrer virtuellen Klinik verfügbar."
            />
          } @else {
            <p-message class="mb-4 block" severity="success" i18n>
              Datensatz erfolgreich übertragen. Die Daten sind in Kürze in ihrer
              virtuellen Klinik verfügbar.
            </p-message>
          }

          <div class="flex flex-row justify-center">
            <p-button
              label="Weiteren Datensatz importieren"
              i18n-label
              severity="primary"
              (onClick)="reload()"
            />
          </div>
        }
      }
    </div>
  `,
  styles: ``,

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BdUiStepProgressComponent,
    BdUiInlineNotificationComponent,
    KostDataImportNewUploadArtifactsComponent,
    Button,
    Message,
    BdUiChainStatusComponent,
  ],
})
export class KostDataImportNewUploadComponent implements OnChanges {
  private readonly _windowService = inject(BdWindowRefService);
  private readonly _chainTracker = inject(BdJobChainTracker);
  private readonly _destroyRef = inject(DestroyRef);

  @Input()
  public processData: KostDataImportProcessData;

  @Input()
  public config: KostDataImportUploadConfig;

  /** The processing the upload started, when the config names its chain. */
  public chain: BdJobChainHandle | null = null;

  public artifacts: { displayName: string; content: Blob }[] = [];

  public readonly state = signal<KostDataImportUploadState | 'done'>('init');
  /** Share of the file sent; `null` until the browser reports a total. */
  public readonly percent = signal<number | null>(null);

  protected readonly steps = computed(() => {
    const state = this.state();
    return state === 'uploading' || state === 'failed'
      ? uploadSteps(state, this.percent(), !!this.config?.chainSubject)
      : [];
  });

  ngOnChanges(changes: SimpleChanges) {
    if (changes['processData']) {
      const output = this.processData?.data?.output;
      if (!output) {
        this.artifacts = [];
        return;
      }
      this.artifacts = [...(output.additionalArtifacts ?? [])];

      if (output.transformed) {
        this.artifacts.push({
          displayName: $localize`Resultierender Datensatz`,
          content: output.transformed,
        });
      }
    }
  }

  public onSubmit(): void {
    this.percent.set(null);
    this.state.set('uploading');

    this.config.uploader().subscribe({
      next: (event) => {
        const update = uploadEvent(event);
        if (update?.kind === 'progress') {
          this.percent.set(update.percent);
        } else if (update?.kind === 'done') {
          this._onUploaded(update.body);
        }
      },
      error: (error: unknown) => {
        console.error(error);
        this.state.set('failed');
      },
    });
  }

  protected heading(chain: BdJobChainResponse | null): string {
    return chainHeading(chain, {
      running: 'Datensatz wird verarbeitet',
      succeeded: 'Datensatz in der virtuellen Klinik verfügbar',
    });
  }

  public reload(): void {
    this._windowService.get().location.reload();
  }

  private _onUploaded(uploaded: unknown): void {
    const subject = this.config.chainSubject?.(uploaded);
    if (subject) {
      this.chain = this._chainTracker.trackSubject(subject, {
        destroyRef: this._destroyRef,
      });
    }
    this.state.set('done');
  }
}
